import {
  AdditionalInternationalNumberPurchaseInSchema,
  AvailableNumberApiTelephonyViewsNinjaNumberAvailableNumberListAvailableNumbersRequest,
  AvailableNumberCountryCode,
  NumberAndAddonsPurchaseInSchema,
  NumberPurchaseInSchema,
  PaymentPageOutSchema,
  PurchaseApiPaymentsViewsNinjaPurchaseGetAddonPlansRequest,
  PurchaseApiPaymentsViewsNinjaPurchaseGetSubscriptionPlanRequest,
  TelephonyViewsNinjaNumberAvailableNumberListAvailableNumbersCountryCodeEnum,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import { getAvailableNumbers } from "@/api/requests/availableNumber";
import {
  purchaseAuthInternationalNumber,
  purchaseInternationalNumber,
} from "@/api/requests/internationalPurchase";
import {
  getAddonPlans,
  getSubscriptionPlan,
  purchaseAddon,
  purchaseAuthAddon,
  purchaseNumberAndAddon,
} from "@/api/requests/purchase";
import { useNumberAddressAddOnStore } from "@/stores/add-ons/number-address.store";
import { useAuthStore } from "@/stores/auth.store";
import { useBusinessStore } from "@/stores/businesses.store";
import { useNumberProvisionStore } from "@/stores/number-provision.store";
import { useUtilsStore } from "@/stores/utils.store";
import { AxiosResponse } from "axios";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useAddress } from "./address";
import { cleanPayload } from "./callQueue";
import { trackAmplitudeEvent } from "./integrations/analytics/amplitude";
import { AMPLITUDE_EVENTS } from "./integrations/analytics/events";
import { submitCustomerDotIODetails } from "./queries/customerengagement";
import { validateMobile } from "./utils";

countries.registerLocale(enLocale);

const pageSize = 100;

export const useNumber = () => {
  const utilsStore = useUtilsStore();
  const numberAddressAddOnStore = useNumberAddressAddOnStore();

  const {
    countryCode,
    country,
    numberRegion,
    selectedPeriod,
    stateCode,
    internationalPlans,
    selectedNumber,
    selectedPlan,
    formDetails,
    allPlans,
    allPlansCountries,
    selectedCountry,
  } = storeToRefs(numberAddressAddOnStore);

  const authStore = useAuthStore();

  const numberProvisionStore = useNumberProvisionStore();

  const { selectedAddress } = useAddress();

  const selectedPlanObj = computed(() => {
    if (!selectedPlan.value) {
      return null;
    }

    if (typeof selectedPlan.value === "string") {
      return null;
    }

    return selectedPlan.value;
  });

  const handleFetchNumbers = async (
    payload: AvailableNumberApiTelephonyViewsNinjaNumberAvailableNumberListAvailableNumbersRequest
  ) => {
    try {
      const availableNumbersResponse = await getAvailableNumbers({
        pageSize,
        countryCode:
          countryCode.value as TelephonyViewsNinjaNumberAvailableNumberListAvailableNumbersCountryCodeEnum,
        ...payload,
      });

      return availableNumbersResponse;
    } catch (error) {
      handleError({ error });
    }
  };

  const handleFetchPlans = async (
    payload: PurchaseApiPaymentsViewsNinjaPurchaseGetSubscriptionPlanRequest = {},
    search: string = ""
  ): Promise<void> => {
    try {
      const plansResponse = await getSubscriptionPlan({
        ...payload,
        pageSize: 100,
        search,
      });

      internationalPlans.value = [
        ...plansResponse.data.data.filter(
          (plan) => plan.term == selectedPeriod.value
        ),
      ];
    } catch (error) {
      handleError({ error });
    }
  };

  const handleGetAddonPlans = async (
    payload: PurchaseApiPaymentsViewsNinjaPurchaseGetAddonPlansRequest
  ) => {
    try {
      const response = await getAddonPlans({
        ...payload,
      });

      internationalPlans.value = response?.data?.data;
      return response;
    } catch (error) {
      handleError({ error });
    }
  };

  const handlePurchase = async () => {
    const businessStore = useBusinessStore();
    const businessId = businessStore.currentBusinessId;

    try {
      const planId = selectedPlanObj.value?.id;

      if (!planId) {
        throw new Error("Plan ID is required");
      }

      // Create base payload with common fields
      const basePayload = {
        plan_id: planId,
        payment_mode: "CARD" as const,
        email: formDetails.value?.email || "",
        phone_number: validateMobile(
          formDetails?.value?.phoneNumber,
          formDetails.value?.dialCode
        ),
        quantity: 1,
        first_name: businessId ? null : formDetails?.value.firstName,
        last_name: businessId ? null : formDetails?.value.lastName,
      };

      const payload = cleanPayload(basePayload);

      // Add business info if needed
      const businessInfo = businessId
        ? undefined
        : {
            name: formDetails.value?.businessName || "",
            is_go_global: true,
          };

      // Add selected number if exists
      const numbers = selectedNumber.value
        ? [
            {
              phone_number: selectedNumber.value?.phone_number,
              provider: selectedNumber.value?.provider || "",
              country: selectedNumber.value
                ?.country_code as AvailableNumberCountryCode,
            },
          ]
        : [];

      let res: AxiosResponse<PaymentPageOutSchema>;

      switch (selectedPlanObj.value?.plan_type) {
        case "number_and_addon": {
          const addonPayload: NumberAndAddonsPurchaseInSchema = {
            ...payload,
            numbers,
            addons: [],
            business: businessInfo || {
              name: "",
              is_go_global: true,
            },
          };

          if (selectedAddress.value) {
            utilsStore.isAddressSelected = true;
            const addonId =
              "addon_id" in selectedPlanObj.value
                ? selectedPlanObj.value?.addon_id
                : null;

            if (!addonId) {
              throw new Error("Addon ID is required");
            }

            const addonIdNumber = Number(addonId);
            if (isNaN(addonIdNumber)) {
              throw new Error("Addon ID is not a number");
            }

            addonPayload.addons = [
              {
                plan_id: addonIdNumber,
                quantity: 1,
                addon: [
                  {
                    address_identifier:
                      selectedAddress.value?.address_identifier || "",
                    provider: selectedAddress.value?.provider || "",
                  },
                ],
              },
            ];
          }

          res = await purchaseNumberAndAddon({
            numberAndAddonsPurchaseInSchema: addonPayload,
          });
          break;
        }
        case "number": {
          if (authStore.isAuthenticated) {
            const numberPayload: AdditionalInternationalNumberPurchaseInSchema =
              {
                ...payload,
                numbers,
              };

            if (utilsStore?.newBusinessObject) {
              const businessName =
                "name" in utilsStore.newBusinessObject
                  ? utilsStore?.newBusinessObject?.name
                  : utilsStore?.newBusinessObject?.businessName;

              numberPayload.business = {
                name: businessName,
                is_go_global: true,
              };
            } else {
              numberPayload.business_id = businessId;
            }

            res = await purchaseAuthInternationalNumber({
              additionalInternationalNumberPurchaseInSchema: numberPayload,
            });
          } else {
            const numberPayload: NumberPurchaseInSchema = {
              ...payload,
              numbers,
              business: businessInfo || {
                name: "",
                is_go_global: true,
              },
            };
            res = await purchaseInternationalNumber({
              numberPurchaseInSchema: numberPayload,
            });
          }
          break;
        }
        case "addon": {
          numberProvisionStore.selectedNumber = [];
          const baseAddonPayload = {
            ...payload,
            business_id: authStore.isAuthenticated ? businessId : undefined,
          };

          if (selectedAddress.value) {
            utilsStore.isAddressSelected = true;
          }

          const addressAddon = selectedAddress.value
            ? [
                {
                  address_identifier:
                    selectedAddress.value.address_identifier || "",
                  provider: selectedAddress.value.provider || "",
                },
              ]
            : [];

          if (authStore.isAuthenticated) {
            res = await purchaseAuthAddon({
              additionalAddonPurchaseInSchema: {
                ...baseAddonPayload,
                addon: addressAddon,
                business: businessInfo || {
                  name: "",
                  is_go_global: true,
                },
              },
            });
          } else {
            res = await purchaseAddon({
              addonPurchaseInSchema: {
                ...baseAddonPayload,
                addon: addressAddon,
                business: businessInfo || {
                  name: "",
                  is_go_global: true,
                },
              },
            });
          }
          break;
        }
        default: {
          throw new Error("Invalid plan type");
        }
      }

      // to be able to identify if its go global payment on redirect from paystack
      utilsStore.goGlobalPayment = true;

      if (payload?.phone_number) {
        authStore.mobile = payload.phone_number;
      }

      try {
        void trackAmplitudeEvent(AMPLITUDE_EVENTS.COMPLETED_SETUP_FLOW);
        void trackAmplitudeEvent(AMPLITUDE_EVENTS.CLICKED_MAKE_PAYMENT);

        const customerIOPayload = {
          plan_title: selectedPlanObj.value?.title,
          ...payload,
        };

        void submitCustomerDotIODetails(customerIOPayload);
      } catch (error) {
        handleError({ error, silent: true });
      }

      if (!res.data.temp_authorization_url) {
        throw new Error("Payment URL not received from server");
      }

      window.location.href = res.data.temp_authorization_url;
    } catch (error) {
      handleError({ error });
    }
  };

  const isNumberAndAddon = computed(() => {
    return selectedPlanObj.value?.plan_type === "number_and_addon";
  });

  return {
    countryCode,
    stateCode,
    handleFetchNumbers,
    handleFetchPlans,
    internationalPlans,
    selectedNumber,
    selectedPlan,
    selectedPeriod,
    numberRegion,
    country,
    formDetails,
    handlePurchase,
    isNumberAndAddon,
    allPlans,
    allPlansCountries,
    selectedCountry,
    handleGetAddonPlans,
  };
};
